import {
  type AreaData,
  type AreaRunHistoryEntry,
  type AreaScheduleEntry,
  AREA_STATE_NAMES,
  type AreaStateName,
  type TeamId,
  type UserId,
  isDefined,
  isSystemUser,
} from '@terros/common'

export const ALL_AREA_STATE_NAMES = AREA_STATE_NAMES
export type { AreaStateName }

export type DraftState = { state: 'draft' }

export type ScheduledState = {
  state: 'scheduled'
  teamId?: TeamId
  userIds?: UserId[]
  scheduledDate: number
}

export type AssignedState = {
  state: 'assigned'
  teamId?: TeamId
  userIds?: UserId[]
  lastRunTimestamp?: number
  lastRunCount?: number
}

export type ExpiringState = {
  state: 'expiring'
  teamId?: TeamId
  userIds?: UserId[]
  lastRunTimestamp?: number
  lastRunCount?: number
  expiryDate: number
}

export type UnassignedState = {
  state: 'unassigned'
  teamId?: TeamId
  userIds?: UserId[]
}

export type AreaState = DraftState | ScheduledState | AssignedState | ExpiringState | UnassignedState

export type AreaStateDisplay = {
  label: string
  description: string
}

const AREA_STATE_DISPLAY_MAP: Record<AreaStateName, AreaStateDisplay> = {
  draft: {
    label: 'Draft',
    description: 'Area created but not assigned to anyone.',
  },
  scheduled: {
    label: 'Scheduled',
    description: 'Assignment is scheduled for a future date.',
  },
  assigned: {
    label: 'Assigned',
    description: 'Currently assigned to a team or user.',
  },
  expiring: {
    label: 'Expiring',
    description: 'Assigned with a scheduled unassignment date.',
  },
  unassigned: {
    label: 'Unassigned',
    description: 'Assignment target set but not currently assigned.',
  },
}

export function getAreaStateDisplay(state: AreaStateName): AreaStateDisplay {
  return AREA_STATE_DISPLAY_MAP[state]
}

export const getAreaOwners = (area: AreaData): UserId[] => {
  return area.userIds ?? []
}

export type TimelineEntry = {
  type: 'scheduled' | 'executed'
  action: 'assign' | 'unassign' | 'reassign'
  timestamp: number
  count?: number
  isPast: boolean
  runBy?: UserId
  scheduled?: boolean
  targetTeamId?: TeamId
  targetUserId?: UserId
}

export const AREA_ACTION_LABELS: Record<AreaScheduleEntry['type'], string> = {
  assign: 'Assign',
  unassign: 'Unassign',
  reassign: 'Reassign',
}

// Run history is the source of truth for past actions; schedule entries only describe upcoming ones.
// Matching the two by timestamp mislabeled executions whenever a run landed on a different day than its schedule.
export function buildUnifiedTimeline(area: AreaData): TimelineEntry[] {
  const now = Date.now()
  const upcoming = (area.schedule ?? []).filter((entry) => entry.at > now).map(toScheduledTimelineEntry)
  const executed = (area.runHistory ?? []).map(toExecutedTimelineEntry)
  return [...upcoming, ...executed].sort((a, b) => b.timestamp - a.timestamp)
}

function toScheduledTimelineEntry(entry: AreaScheduleEntry): TimelineEntry {
  const target = getScheduleEntryTarget(entry)
  return {
    type: 'scheduled',
    action: entry.type,
    timestamp: entry.at,
    isPast: false,
    runBy: entry.by,
    targetTeamId: target.teamId,
    targetUserId: target.userId,
  }
}

function toExecutedTimelineEntry(run: AreaRunHistoryEntry): TimelineEntry {
  return {
    type: 'executed',
    action: run.type ?? 'assign',
    timestamp: run.timestamp,
    count: run.count,
    isPast: true,
    runBy: run.runBy,
    scheduled: run.scheduled,
    targetTeamId: run.teamId,
    targetUserId: run.userIds?.at(0),
  }
}

export function getUpcomingEntries(timeline: TimelineEntry[]): TimelineEntry[] {
  return timeline.filter((e) => !e.isPast)
}

export function getPastEntries(timeline: TimelineEntry[]): TimelineEntry[] {
  return timeline.filter((e) => e.isPast)
}

export function isScheduledTimelineEntry(entry: TimelineEntry): boolean {
  if (isDefined(entry.scheduled)) return entry.scheduled
  return isDefined(entry.runBy) && isSystemUser(entry.runBy)
}

function getScheduleEntryTarget(scheduleEntry: AreaScheduleEntry): { teamId?: TeamId; userId?: UserId } {
  if (scheduleEntry.type !== 'reassign') return {}

  return {
    teamId: scheduleEntry.teamId,
    userId: scheduleEntry.userIds?.at(0),
  }
}
